import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Info as InfoIcon } from 'react-feather'
import { Controller, useForm } from 'react-hook-form'
import {
  Grid,
  TextField,
  Box,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Radio,
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@material-ui/core'

import { Autocomplete, Label, Select as SelectComponent } from 'components'
import DataProcessTemplateForm from 'views/DataProcesses/components/DataProcessTemplateForm'

import helpers from 'helpers'

import useDataProcess from 'hooks/useDataProcess'
import useDataProcessTemplateQuestion from 'hooks/useDataProcessTemplateQuestion'
import useAssetInventory from 'hooks/useAssetInventory'

import dataLifeCycleSchema from '../schema'
import constants from 'constants/index'
import useStyles from './styles'
import theme from 'theme'

const Form = ({ onSubmit = () => {}, dataLifeCycle = {}, dataProcess }) => {
  const [appliedDefaultValues, setAppliedDefaultValues] = useState(false)
  const [hasNewAssetInventory, setHasNewAssetInventory] = useState(false)

  const volumetryOptions = helpers.dataProcess.selectVolumetryOptions()

  const { internalAllocationModes, discardModes, recoveries, protections } =
    useDataProcess()
  const {
    assetInventories,
    loadingInventories,
    assetInventoryCategories,
    loadingCategories,
    setAssetInventoryProps,
  } = useAssetInventory()
  const {
    triggerValidation: triggerTemplateValidation,
    getValues: getTemplateValues,
  } = useDataProcessTemplateQuestion()
  const classes = useStyles()

  const storageTypeFormated = dataLifeCycle?.storageType

  const filterAssetInventoryCategories = assetInventoryCategories.map(
    (category) => ({
      id: category.id,
      name: category.name,
    }),
  )

  const filterAssetInventories = assetInventories.map((inventory) => ({
    id: inventory.id,
    name: inventory.name,
  }))

  const defaultAssetInventory = filterAssetInventories.find(
    (inventory) => inventory.id === dataLifeCycle?.assetInventory?.id,
  )

  const defaultAssetInventoryCategory = filterAssetInventoryCategories.find(
    (category) =>
      category.id === dataLifeCycle?.assetInventoryCategory?.id ||
      category.id === dataLifeCycle?.assetInventory?.assetInventoryCategory?.id,
  )

  const {
    errors,
    watch,
    control,
    setError,
    triggerValidation: triggerFormValidation,
    getValues,
    setValue,
  } = useForm({
    validationSchema: dataLifeCycleSchema,
    defaultValues: {
      assetInventory: defaultAssetInventory || null,
      assetInventoryCategory: defaultAssetInventoryCategory || null,
      internalAllocationModeId: dataLifeCycle?.internalAllocationMode?.id || '',
      storageType: `${storageTypeFormated}` ?? '',
      value: dataLifeCycle?.value || '',
      volumetry: dataLifeCycle?.volumetry || '',
      retentionFinality: dataLifeCycle?.retentionFinality || '',
      discardModeId: dataLifeCycle?.discardMode?.id || '',
      recoveryId: dataLifeCycle?.recovery?.id || '',
      protectionId: dataLifeCycle?.protection?.id || '',
      anotherAssetInventory: '',
      anotherDiscardMode: dataLifeCycle?.discardMode?.id || '',
      anotherRecovery: dataLifeCycle?.recovery?.id || '',
    },
  })

  const {
    STORY_MODE_PERMANENT_TYPE,
    STORAGE_DEFINED_TYPE,
    STORAGE_UNDEFINED_TYPE,
    STORAGE_PERMANENT_TYPE,
    STORY_MODE_DEFINED_TYPE,
  } = constants.dataProcess

  const assetInventoryCategory = watch('assetInventoryCategory')

  const handleAnotherAssetInventory = () => {
    setAppliedDefaultValues(false)
    setHasNewAssetInventory((prevState) => !prevState)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const templateValidation = await triggerTemplateValidation()
    const dataCollectedValidation = await triggerValidation()

    if (templateValidation && dataCollectedValidation) {
      onSubmit(getValues(), getTemplateValues())
    }
  }

  const triggerValidation = async () => {
    const valueDataProcess = dataProcess?.value
    const volumetryDataProcess = dataProcess?.volumetry
    const storageMode = dataProcess?.storageMode

    const dataCollectedValidation = await triggerFormValidation()

    if (storageMode === STORY_MODE_DEFINED_TYPE) {
      let formattedStorageTimeDataProcess =
        helpers.dataProcess.storageTimeValidation(
          valueDataProcess,
          volumetryDataProcess,
        )

      let formattedStorageTimeLifeCycle =
        helpers.dataProcess.storageTimeValidation(
          getValues('value'),
          getValues('volumetry'),
        )

      if (formattedStorageTimeDataProcess < formattedStorageTimeLifeCycle) {
        setError('value', {
          type: {
            invalid:
              'Insira uma data menor do que a informada nas informações gerais do processo',
          },
        })

        setError('volumetry', {
          type: {
            invalid:
              'Insira uma data menor do que a informada nas informações gerais do processo',
          },
        })
        return false
      }
    }

    return dataCollectedValidation
  }

  // Realiza a busca do ativo de acordo com a categoria selecionada
  useEffect(() => {
    if (!hasNewAssetInventory) {
      setValue('assetInventory', null)

      if (assetInventoryCategory?.id) {
        setAssetInventoryProps({
          minimal: true,
          assetInventoryCategoryId: assetInventoryCategory?.id,
        })
      }
    }
  }, [assetInventoryCategory])

  // Aplica os valores padrões ao carregar a busca dos ativos
  useEffect(() => {
    if (!appliedDefaultValues && !!defaultAssetInventory) {
      setAppliedDefaultValues(true)
      setValue('assetInventory', defaultAssetInventory)
    }
  }, [defaultAssetInventory])

  // Limpa os filtros ao desmontar o componente para forçar a re-consulta
  useEffect(() => {
    return () => {
      setAppliedDefaultValues(false)
      setAssetInventoryProps({})
    }
  }, [])

  return (
    <form onSubmit={handleSubmit} id="data-life-cycle-form">
      <Grid
        container
        spacing={1}
        justifyContent="flex-start"
        className={classes.root}
      >
        <Grid item xl={6} lg={6} md={6} xs={12}>
          <Controller control={control} name="dataLifeCycleId" as={<></>} />
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            {!hasNewAssetInventory && (
              <Fragment>
                <Label
                  title="Categoria de ativos"
                  description="Qual a categoria do ativo pertencente?"
                  item
                >
                  <Controller
                    control={control}
                    mode="onChange"
                    name="assetInventoryCategory"
                    onChange={([, data]) => data || null}
                    as={
                      <Autocomplete
                        options={filterAssetInventoryCategories}
                        getOptionLabel={(option) => option.name || ''}
                        getOptionSelected={(option, value) =>
                          option.id === value.id || value.id === 0
                        }
                        textFieldProps={{
                          placeholder:
                            'Em qual categoria do ativos esse salvaguarda se enquadra?',
                          error: !!errors.assetInventoryCategory,
                          helperText:
                            errors?.assetInventoryCategory?.message ||
                            errors?.assetInventoryCategory?.id?.message,
                        }}
                        loading={loadingCategories}
                      />
                    }
                  />
                </Label>
                {!!assetInventoryCategory?.id && (
                  <Controller
                    control={control}
                    mode="onChange"
                    name="assetInventory"
                    onChange={([, data]) => data || null}
                    as={
                      <Autocomplete
                        options={filterAssetInventories}
                        getOptionLabel={(option) => option.name || ''}
                        getOptionSelected={(option, value) =>
                          option.id === value.id || value.id === 0
                        }
                        textFieldProps={{
                          label: 'Ativos',
                          placeholder:
                            'Em qual ativo esse salvaguarda se enquadra?',
                          error: !!errors.assetInventory,
                          helperText:
                            errors?.assetInventory?.message ||
                            errors?.assetInventory?.id?.message,
                        }}
                        loading={loadingInventories}
                      />
                    }
                  />
                )}
              </Fragment>
            )}
          </Box>
          {hasNewAssetInventory && (
            <Label
              title="Novo inventário de ativos"
              description="Qual o nome do novo inventário de ativos?"
              item
            >
              <Controller
                control={control}
                name="anotherAssetInventory"
                as={
                  <TextField
                    placeholder="Defina o nome do novo inventário de ativos"
                    variant="outlined"
                    error={!!errors.anotherAssetInventory}
                    helperText={errors?.anotherAssetInventory?.message}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Recomenda-se editar posteriormente esse inventário na tela de inventários de ativos para completar as informações necessárias.">
                            <IconButton
                              className={classes.infoIcon}
                              color="primary"
                              size="small"
                            >
                              <InfoIcon size={18} />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                }
              />
            </Label>
          )}
          <Typography
            variant="caption"
            color="secondary"
            className={classes.link}
            onClick={handleAnotherAssetInventory}
          >
            {hasNewAssetInventory
              ? 'Voltar para a seleção de ativos existentes'
              : 'Não encontrei os ativos desejados'}
          </Typography>
        </Grid>
        <Grid item xl={6} lg={6} md={6} xs={12}>
          <Label
            title="Ambiente de alocação"
            description="A informação é alocada em que ambiente?"
            item
          >
            <Controller
              name="internalAllocationModeId"
              control={control}
              as={
                <SelectComponent
                  items={internalAllocationModes}
                  error={!!errors.internalAllocationModeId}
                  helperText={errors?.internalAllocationModeId?.message}
                />
              }
              mode="onBlur"
            />
          </Label>
        </Grid>
        <Grid item lg={12} xs={12}>
          <Box mt={1}>
            <Label
              title="Tempo de armazenamento"
              description="Por quanto tempo esses dados serão armazenados e o que é feito com esses dados quando o ciclo de vida termina?"
              error={!!errors.storageType}
              item
            >
              <Controller
                control={control}
                name="storageType"
                as={
                  <RadioGroup>
                    <FormControlLabel
                      key={`${STORAGE_DEFINED_TYPE}`}
                      value={`${STORAGE_DEFINED_TYPE}`}
                      control={<Radio />}
                      label="Definido"
                    />
                    <FormControlLabel
                      key={`${STORAGE_UNDEFINED_TYPE}`}
                      value={`${STORAGE_UNDEFINED_TYPE}`}
                      control={<Radio />}
                      label="Indefinido"
                    />
                    {dataProcess?.storageMode === STORY_MODE_PERMANENT_TYPE && (
                      <FormControlLabel
                        key={`${STORAGE_PERMANENT_TYPE}`}
                        value={`${STORAGE_PERMANENT_TYPE}`}
                        control={<Radio />}
                        label="Permanente"
                      />
                    )}
                  </RadioGroup>
                }
                mode="onChange"
              />
              {!!errors.storageType && (
                <FormHelperText error>
                  {errors.storageType.message}
                </FormHelperText>
              )}
            </Label>
            {watch('storageType') === `${STORAGE_DEFINED_TYPE}` && (
              <>
                <Grid container spacing={1}>
                  <Grid item lg={6} xs={12}>
                    <Label
                      title="Valor"
                      description="Qual é a quantidade do tempo?"
                      item
                    >
                      <Controller
                        name="value"
                        control={control}
                        as={
                          <TextField
                            type="number"
                            variant="outlined"
                            error={!!errors.value}
                            fullWidth
                            helperText={
                              errors?.value?.message ||
                              errors?.value?.types?.type?.invalid
                            }
                          />
                        }
                        mode="onBlur"
                        onChange={([e]) =>
                          e.target.value < 0 ? '0' : e.target.value
                        }
                      />
                    </Label>
                  </Grid>

                  <Grid item lg={6} xs={12}>
                    <Label
                      title="Unidade de tempo"
                      description="Qual é o formato do tempo?"
                      item
                    >
                      <Controller
                        name="volumetry"
                        control={control}
                        as={
                          <SelectComponent
                            items={volumetryOptions}
                            error={!!errors.volumetry}
                            fullWidth
                            helperText={
                              errors.volumetry?.message ||
                              errors?.value?.types?.type?.invalid
                            }
                          />
                        }
                        mode="onBlur"
                      />
                    </Label>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption">
                      *Atenção: o tempo informado deve ser menor ou igual ao das
                      informações gerais do processo
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Label
            title="Justificativa de retenção"
            description="Justifique porque o registro é retido no departamento pelo tempo informado."
            item
          >
            <Controller
              name="retentionFinality"
              control={control}
              as={
                <TextField
                  multiline
                  rows={3}
                  color="primary"
                  variant="outlined"
                  error={!!errors.retentionFinality}
                  helperText={errors?.retentionFinality?.message}
                />
              }
              mode="onChange"
              fullWidth
            />
          </Label>
        </Grid>
        {watch('storageType') !== `${STORAGE_PERMANENT_TYPE}` && (
          <Grid item xs={12}>
            <Label
              title="Como os dados são dispostos?"
              description="O que é feito com esses dados quando o ciclo de vida termina? Exemplo: deletar, destruir, etc."
              item
            >
              <Controller
                name="discardModeId"
                control={control}
                as={
                  <SelectComponent
                    items={discardModes}
                    color="primary"
                    anotherOption={true}
                    error={!!errors.discardModeId}
                    helperText={errors?.discardModeId?.message}
                    fullWidth
                  />
                }
                mode="onBlur"
              />
            </Label>
            {watch('discardModeId') === 'another' && (
              <Box mt={1}>
                <Label description="Outro: como os dados são dispostos?" item>
                  <Controller
                    name="anotherDiscardMode"
                    control={control}
                    as={
                      <TextField
                        color="primary"
                        variant="outlined"
                        error={!!errors.anotherDiscardMode}
                        helperText={errors?.anotherDiscardMode?.message}
                        fullWidth
                      />
                    }
                  />
                </Label>
              </Box>
            )}
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1}>
        <Grid item md={6} xs={12}>
          <Label
            className={classes.gridItem}
            containerProps={{ height: '100%', alignContent: 'end' }}
            title="Forma de recuperação"
            description="Qual dado é utilizado para localizar e acessar/recuperar o registro?"
            item
          >
            <Controller
              name="recoveryId"
              control={control}
              as={
                <SelectComponent
                  items={recoveries}
                  color="primary"
                  anotherOption={true}
                  error={!!errors.recoveryId}
                  helperText={errors?.recoveryId?.message}
                />
              }
              mode="onBlur"
            />
          </Label>
        </Grid>
        <Grid item md={6} xs={12}>
          <Label
            title="Forma de proteção"
            description="A informação é protegida fisicamente(impressa) ou eletronicamente (sistemas, e-mail, servidor,etc)?"
            item
          >
            <Controller
              name="protectionId"
              control={control}
              as={
                <SelectComponent
                  items={protections}
                  color="primary"
                  error={!!errors.protectionId}
                  helperText={errors?.protectionId?.message}
                  name="protectionId"
                />
              }
              mode="onBlur"
            />
          </Label>
        </Grid>
        {watch('recoveryId') === 'another' && (
          <Grid item xl={6}>
            <Box mr={0}>
              <Label description="Outro: forma de recuperação" item>
                <Controller
                  name="anotherRecovery"
                  control={control}
                  as={
                    <TextField
                      color="primary"
                      variant="outlined"
                      error={!!errors.anotherRecovery}
                      helperText={errors.anotherRecovery?.message}
                    />
                  }
                />
              </Label>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <DataProcessTemplateForm step="data_collected" />
        </Grid>
      </Grid>
    </form>
  )
}

Form.propTypes = {
  onSubmit: PropTypes.func,
  dataLifeCycle: PropTypes.object,
  storageMode: PropTypes.string.isRequired,
}

export default Form
